import { createContext, use, useEffect, useMemo, type ReactNode } from "react";
import type { Brand } from "@syllarys/contracts";
import { useLocation } from "react-router-dom";
import { useEms } from "./EmsContext";
import { resolveBrandForHost, resolveBrandForRoute } from "./brand";

interface BrandContextValue {
  brand: Brand;
  isWhiteLabel: boolean;
}

const BrandContext = createContext<BrandContextValue | null>(null);

function brandRequestedForLocalPreview(): string {
  if (!import.meta.env.DEV) return "";
  const hostname = window.location.hostname.toLowerCase();
  if (hostname !== "localhost" && hostname !== "127.0.0.1") return "";
  return new URLSearchParams(window.location.search).get("brand")?.trim() ?? "";
}

function updateFavicon(url: string): void {
  const links = document.querySelectorAll<HTMLLinkElement>(
    'link[rel="icon"], link[rel="shortcut icon"]',
  );
  links.forEach((link) => {
    link.href = url;
  });
}

/**
 * Les marques publiques arrivent de l'API avant toute session : tant qu'elles
 * ne sont pas chargées, aucune identité visuelle n'est inventée.
 */
export function BrandProvider({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const { db, session, apiState, retryApi } = useEms();
  const location = useLocation();
  const hostBrand = useMemo(() => {
    if (!db.brands.length) return null;
    try {
      return resolveBrandForHost(
        db.brands,
        window.location.hostname,
        brandRequestedForLocalPreview(),
      );
    } catch {
      return null;
    }
  }, [db.brands]);
  const brand = useMemo(
    () =>
      hostBrand
        ? resolveBrandForRoute(hostBrand, session?.brand, location.pathname)
        : null,
    [hostBrand, location.pathname, session?.brand],
  );

  useEffect(() => {
    if (!brand) return;
    document.title = brand.displayName;
    document.documentElement.dataset.brand = brand.slug;
    document.documentElement.style.setProperty(
      "--brand-primary",
      brand.primaryColor,
    );
    document.documentElement.style.setProperty(
      "--brand-secondary",
      brand.secondaryColor,
    );
    updateFavicon(brand.faviconUrl);
  }, [brand]);

  const value = useMemo<BrandContextValue | null>(
    () => (brand ? { brand, isWhiteLabel: !brand.isDefault } : null),
    [brand],
  );

  if (!value) {
    if (apiState !== "loading") {
      return (
        <div className="api-state-screen" role="alert">
          <h1>
            {apiState === "ready"
              ? "Marque introuvable"
              : "Service indisponible"}
          </h1>
          <p>
            {apiState === "ready"
              ? "Aucune marque ne correspond à cette adresse. Vérifiez le lien utilisé ou contactez votre administrateur."
              : "Impossible de charger votre espace pour le moment. Vous pouvez réessayer sans perdre votre lien."}
          </p>
          <button className="primary-button" onClick={() => void retryApi()}>
            Réessayer
          </button>
        </div>
      );
    }
    return (
      <div className="loading-screen" role="status" aria-live="polite">
        <p>Chargement de la marque…</p>
      </div>
    );
  }
  return <BrandContext value={value}>{children}</BrandContext>;
}

export function useBrand(): BrandContextValue {
  const context = use(BrandContext);
  if (!context) throw new Error("useBrand must be used inside BrandProvider.");
  return context;
}
